import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import { Field } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Icon, IconButton } from "@mui/material";
import { show } from "redux-modal";

import BMXAsynchronous from "../../../../../Components/Inputs/BMXAsynchronous/BMXAsynchronous";
import BMXDatePicker from "../../../../../Components/Inputs/BMXDatePicker/BMXDatePicker";
import BMXSelectField from "../../../../../Components/Inputs/BMXSelectField/BMXSelectField";
import BMXTextField from "../../../../../Components/Inputs/BMXTextField/BMXTextField";
import BMXSeparator from "../../../../../Components/Separator/BMXSeparator";
import { GlobalAuthFetch } from "../../../../../Utils/GlobalAuthFetch/GlobalAuthFetch";
import { updateDashboardByName } from "../../../../../Utils/Sockets";

const REGISTRY_NAME = "contact";

const ComplaintsFormStepOne = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [contractors, setContractors] = useState([]);
  const [deviceOptions, setDeviceOptions] = useState([]);

  const { values, form, update } = props;

  // 1. Pobieranie kontrahentów (kontaktów)
  const getContractorsData = async (searchTerm) => {
    if (searchTerm?.length > 0) {
      const res = await GlobalAuthFetch({
        path: `/contact/findByName/${searchTerm}`,
      });
      const list = res?.payload?.data?.map((contact) => ({
        uuid: contact.uuid,
        value: contact.uuid,
        name: contact.name,
        ...contact,
      }));

      if (list?.length > 0) {
        setContractors(list);
        return list;
      }
      setContractors([]);
      return [];
    }
    return [];
  };

  const getSelectedContractor = (contractor) => {
    if (!contractor) return null;
    return contractors.find(
      (el) => el.uuid === contractor.value || el.uuid === contractor.uuid,
    );
  };

  const clearDevice = () => {
    form.change("device", null);
    form.change("deviceDetails", "");
    form.change("deviceInvoice", "");
    form.change("deviceDateOfBuy", null);
    form.change("deviceWarrantyTo", null);
    setDeviceOptions([]);
  };

  // 2. Pobieranie urządzeń (po numerze seryjnym)
  const getDevices = async (searchTerm) => {
    const cleanSearchTerm = searchTerm?.trim();

    if (
      !cleanSearchTerm?.length ||
      !getSelectedContractor(values?.contractor)?.uuid
    ) {
      setDeviceOptions([]);
      return [];
    }

    const params = new URLSearchParams({
      serialNumber: cleanSearchTerm.toUpperCase(), // Używamy oczyszczonego terminu
      contactUuid: getSelectedContractor(values?.contractor)?.uuid,
    });

    const res = await GlobalAuthFetch({
      path: `/device/findBySNAndContact?${params.toString()}`,
    });

    const list =
      res?.payload?.data?.map((device) => ({
        ...device,
        uuid: device.uuid,
        value: device.uuid,
        serialNumber: device.serialNumber,
      })) ?? [];

    setDeviceOptions(list);
    return list;
  };

  return (
    <div style={{ justifyContent: "flex-start", paddingTop: "10px" }}>
      {/* ----------------- DANE PODSTAWOWE ----------------- */}
      <BMXSeparator title={t("global:complaints.basicInformation")} />

      <Row>
        <Col xs={6}>
          <Field
            component={BMXTextField}
            label={`${t("global:complaints.complaintTitle")}*`}
            name="complaintTitle"
            data-cy="complaintTitle"
          />
        </Col>

        <Col xs={6}>
          <Field
            currentValues={values}
            className="contractor"
            component={BMXAsynchronous}
            getOptionLabel={(option) => {
              if (option.name) return option.name;
              return option;
            }}
            label={`${t("global:complaints.contractor")}*`}
            name="contractor"
            options={contractors}
            getData={getContractorsData}
            disabled={update}
            data-cy="contractor"
            disablePortal={true}
            afterIcon="add"
            afterIconSize="18px"
            afterIconClick={() => {
              const contractorUuid =
                values?.contractor?.uuid || values?.contractor?.value;
              const updateSingleRegistry = () =>
                updateDashboardByName(REGISTRY_NAME, contractorUuid);

              dispatch(
                show("ContactsModal", {
                  loadData: async () => {
                    await getContractorsData(values?.contractor?.name);
                  },
                  header: t("global:complaints.contractorAdd"),
                  updateSingleRegistry,
                  uuid: contractorUuid,
                }),
              );
            }}
            onChange={(contractor) => {
              form.change("contractor", contractor);
              clearDevice();

              form.change("representative", null);
              form.change("representativePhone", "");
              form.change("representativeEmail", "");
            }}
            onClose={() => {
              form.change("representative", null);
              form.change("representativePhone", "");
              form.change("representativeEmail", "");
            }}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={6}>
          <Field
            component={BMXDatePicker}
            label={`${t("global:complaints.receivedDate")}*`}
            name="receivedDate"
            data-cy="receivedDate"
          />
        </Col>
        <Col xs={6}>
          <Field
            component={BMXDatePicker}
            label={`${t("global:complaints.plannedClosingDate")}`}
            name="plannedClosingDate"
            data-cy="plannedClosingDate"
            minDate={
              values?.receivedDate ? new Date(values.receivedDate) : new Date()
            }
          />
        </Col>
      </Row>

      {/* ----------------- PRZEDMIOT REKLAMACJI ----------------- */}
      <BMXSeparator title={t("global:complaints.complaintSubject")} />

      <Row>
        {/* Wybór numeru seryjnego oparty dokladnie na ServiceDeviceSelector */}
        <Col
          xs={6}
          style={{
            display: "inline-flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "5px",
          }}
        >
          <Field
            style={{ width: "100%" }}
            currentValues={values}
            className="step-one-device-select"
            component={BMXAsynchronous}
            label={`${t("global:complaints.serialNumber")}*`}
            name="device"
            disabled={update || !values?.contractor}
            options={deviceOptions}
            getData={getDevices}
            getOptionLabel={(option) => option?.serialNumber?.trim() || option}
            disablePortal
            data-cy="device"
            onChange={(device) => {
              if (!device) {
                form.change("device", null);
                form.change("deviceDetails", "");
                form.change("deviceInvoice", "");
                form.change("deviceDateOfBuy", null);
                form.change("deviceWarrantyTo", null);
                return;
              }

              form.change("device", device);

              form.change("deviceInvoice", device.deviceInvoice || "");
              form.change("deviceDateOfBuy", device.deviceDateOfBuy || "");
              form.change("deviceWarrantyTo", device.deviceWarrantyTo || "");
            }}
          />

          <IconButton
            style={{ padding: "0" }}
            onClick={() =>
              dispatch(
                show("DeviceModal", {
                  header: t("global:devices.add"),
                  updateSingleRegistry: () => updateDashboardByName("device"),
                }),
              )
            }
          >
            <Icon style={{ fontSize: "20px" }} className="dark-grey text">
              add
            </Icon>
          </IconButton>
        </Col>
        <Col xs={6}>
          <Field
            name="deviceDetails"
            component={BMXTextField}
            label={t("global:complaints.deviceDetails")}
            input={{
              value: (() => {
                const dev = values?.device;
                if (!dev) return "";

                // Wyciąganie bezpiecznych wartości tekstowych
                const model = dev.printerModel?.modelName || "";

                const manufacturer =
                  typeof dev.printerModel?.manufacturer === "object"
                    ? dev.printerModel?.manufacturer?.name ||
                      dev.printerModel?.manufacturer?.entryName ||
                      ""
                    : dev.manufacturer || "";

                const deviceType =
                  typeof dev.printerModel?.deviceType === "object"
                    ? dev.printerModel?.deviceType?.name ||
                      dev.printerModel?.deviceType?.entryName ||
                      ""
                    : dev.deviceType || "";

                return [model, manufacturer, deviceType]
                  .filter(Boolean)
                  .join(", ");
              })(),
            }}
            readOnly
          />
        </Col>
      </Row>

      <Row>
        <Col xs={4}>
          <Field
            component={BMXTextField}
            label={t("global:complaints.deviceInvoice")}
            name="deviceInvoice"
            data-cy="deviceInvoice"
          />
        </Col>
        <Col xs={4}>
          <Field
            component={BMXDatePicker}
            label={t("global:complaints.deviceDateOfBuy")}
            name="deviceDateOfBuy"
            data-cy="deviceDateOfBuy"
          />
        </Col>
        <Col xs={4}>
          <Field
            component={BMXDatePicker}
            label={t("global:complaints.deviceWarrantyTo")}
            name="deviceWarrantyTo"
            data-cy="deviceWarrantyTo"
          />
        </Col>
      </Row>

      {/* ----------------- OSOBA DO KONTAKTU ----------------- */}
      <BMXSeparator title={t("global:complaints.contactPerson")} />

      <Row>
        <Col xs={4}>
          <Field
            data-cy="representative"
            beforeIcon="people"
            afterIcon="add"
            afterIconSize="18px"
            afterIconClick={() => {
              const contractorUuid =
                values?.contractor?.uuid || values?.contractor?.value;
              dispatch(
                show("RepresentativesModal", {
                  loadData: async () => {
                    await getContractorsData(values?.contractor?.name);
                  },
                  header: t("global:complaints.representativeAdd"),
                  updateSingleRegistry: () =>
                    updateDashboardByName("contact", contractorUuid),
                  uuid: contractorUuid,
                }),
              );
            }}
            disabled={!values?.contractor}
            component={BMXSelectField}
            values={getSelectedContractor(
              values?.contractor,
            )?.representatives?.map((el) => ({
              value: el.uuid,
              name: `${el.name || ""} ${el.lastname || ""}`.trim(),
              ...el,
            }))}
            label={t("global:complaints.representative")}
            name="representative"
            onChange={(selectedRep) => {
              form.change("representative", selectedRep);
              const contractor = getSelectedContractor(values?.contractor);
              const repData = contractor?.representatives?.find(
                (r) => r.uuid === (selectedRep?.value || selectedRep),
              );

              if (repData) {
                form.change("representativePhone", repData.phoneNumber || "");
                form.change("representativeEmail", repData.email || "");
              } else {
                form.change("representativePhone", "");
                form.change("representativeEmail", "");
              }
            }}
          />
        </Col>
        <Col xs={4}>
          <Field
            component={BMXTextField}
            label={t("global:complaints.representativePhone")}
            name="representativePhone"
            data-cy="representativePhone"
          />
        </Col>
        <Col xs={4}>
          <Field
            component={BMXTextField}
            label={t("global:complaints.representativeEmail")}
            name="representativeEmail"
            data-cy="representativeEmail"
          />
        </Col>
      </Row>
    </div>
  );
};

export default ComplaintsFormStepOne;
